import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type React from 'react'

import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined'
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined'
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined'
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined'
import AllInboxOutlinedIcon from '@mui/icons-material/AllInboxOutlined'
import ElevatorOutlinedIcon from '@mui/icons-material/ElevatorOutlined'
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined'
import KitchenOutlinedIcon from '@mui/icons-material/KitchenOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined'
import PlumbingOutlinedIcon from '@mui/icons-material/PlumbingOutlined'
import HvacOutlinedIcon from '@mui/icons-material/HvacOutlined'
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined'
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'
import PowerOutlinedIcon from '@mui/icons-material/PowerOutlined'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined'
import SolarPowerOutlinedIcon from '@mui/icons-material/SolarPowerOutlined'
import AirOutlinedIcon from '@mui/icons-material/AirOutlined'
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined'
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined'
import CarpenterOutlinedIcon from '@mui/icons-material/CarpenterOutlined'
import RoofingOutlinedIcon from '@mui/icons-material/RoofingOutlined'
import GasMeterOutlinedIcon from '@mui/icons-material/GasMeterOutlined'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'

export type IconKey = string

export interface IconEntry {
  key: string
  label: string
  Component: React.ElementType
}

export const ICON_REGISTRY: IconEntry[] = [
  { key: 'category',       label: 'Category',       Component: CategoryOutlinedIcon },
  { key: 'hvac',           label: 'HVAC',           Component: HvacOutlinedIcon },
  { key: 'snowflake',      label: 'Cooling',        Component: AcUnitOutlinedIcon },
  { key: 'air',            label: 'Air',            Component: AirOutlinedIcon },
  { key: 'water',          label: 'Water',          Component: WaterDropOutlinedIcon },
  { key: 'plumbing',       label: 'Plumbing',       Component: PlumbingOutlinedIcon },
  { key: 'electric',       label: 'Electrical',     Component: ElectricBoltOutlinedIcon },
  { key: 'power',          label: 'Power',          Component: PowerOutlinedIcon },
  { key: 'lightbulb',      label: 'Lighting',       Component: LightbulbOutlinedIcon },
  { key: 'solar',          label: 'Solar',          Component: SolarPowerOutlinedIcon },
  { key: 'fire',           label: 'Fire',           Component: LocalFireDepartmentOutlinedIcon },
  { key: 'security',       label: 'Security',       Component: SecurityOutlinedIcon },
  { key: 'server',         label: 'Server',         Component: DnsOutlinedIcon },
  { key: 'storage',        label: 'Storage',        Component: StorageOutlinedIcon },
  { key: 'router',         label: 'Network',        Component: RouterOutlinedIcon },
  { key: 'sensor',         label: 'Sensor',         Component: SensorsOutlinedIcon },
  { key: 'manufacturing',  label: 'Manufacturing',  Component: PrecisionManufacturingOutlinedIcon },
  { key: 'engineering',    label: 'Engineering',     Component: EngineeringOutlinedIcon },
  { key: 'build',          label: 'Tools',          Component: BuildOutlinedIcon },
  { key: 'construction',   label: 'Construction',   Component: ConstructionOutlinedIcon },
  { key: 'carpenter',      label: 'Carpentry',      Component: CarpenterOutlinedIcon },
  { key: 'settings',       label: 'Mechanical',     Component: SettingsOutlinedIcon },
  { key: 'speed',          label: 'Gauge',          Component: SpeedOutlinedIcon },
  { key: 'gasMeter',       label: 'Gas meter',      Component: GasMeterOutlinedIcon },
  { key: 'elevator',       label: 'Elevator',       Component: ElevatorOutlinedIcon },
  { key: 'shipping',       label: 'Shipping',       Component: LocalShippingOutlinedIcon },
  { key: 'car',            label: 'Vehicle',        Component: DirectionsCarOutlinedIcon },
  { key: 'gas',            label: 'Fuel',           Component: LocalGasStationOutlinedIcon },
  { key: 'kitchen',        label: 'Refrigeration',  Component: KitchenOutlinedIcon },
  { key: 'packaging',      label: 'Packaging',      Component: AllInboxOutlinedIcon },
  { key: 'cleaning',       label: 'Cleaning',       Component: CleaningServicesOutlinedIcon },
  { key: 'medical',        label: 'Medical',        Component: MedicalServicesOutlinedIcon },
  { key: 'roofing',        label: 'Roofing',        Component: RoofingOutlinedIcon },
  { key: 'hub',            label: 'Hub',            Component: HubOutlinedIcon },
]

const ICON_MAP = new Map(ICON_REGISTRY.map((e) => [e.key, e]))

// Auto-guess icon from category name
const NAME_TO_ICON: Record<string, string> = {
  hvac: 'hvac', plumbing: 'plumbing', electrical: 'electric',
  'it infrastructure': 'server', 'fire safety': 'fire',
  mechanical: 'settings', packaging: 'packaging', elevator: 'elevator',
  security: 'security', refrigeration: 'kitchen',
}

// Persisted store: categoryId → iconKey
interface IconStore {
  icons: Record<string, string>
  setIcon: (categoryId: string, iconKey: string) => void
}

export const useCategoryIconStore = create<IconStore>()(
  persist(
    (set) => ({
      icons: {},
      setIcon: (categoryId, iconKey) => set((s) => ({ icons: { ...s.icons, [categoryId]: iconKey } })),
    }),
    { name: 'cmms-category-icons' },
  ),
)

export function getCategoryIcon(categoryId: string, categoryName: string): React.ElementType {
  const store = useCategoryIconStore.getState()
  const override = store.icons[categoryId]
  if (override) {
    const entry = ICON_MAP.get(override)
    if (entry) return entry.Component
  }
  const guess = NAME_TO_ICON[categoryName.toLowerCase()]
  if (guess) {
    const entry = ICON_MAP.get(guess)
    if (entry) return entry.Component
  }
  return CategoryOutlinedIcon
}
